import {
  Article,
  ArticleAction,
  ArticleFooter,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Badge } from "@babackapp/customer-ui/components/badge";
import { Checkbox } from "@babackapp/customer-ui/components/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { InfoIcon } from "lucide-react";
import { getPaidPriceInCents } from "../lib/paid-price.lib";
import type { OrderLineItem } from "../types";
import { formatPrice } from "../utils/format-price";
import { parseLineItemPriceInCents } from "../utils/get-line-item-price";

type ArticleRowProps = {
  item: OrderLineItem;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  badge?: string;
  currency?: string;
  /** True when the current session is an authenticated Baback dashboard user (merchant or staff) — gates SKU visibility. */
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
};

export function ArticleRow({
  item,
  checked,
  onToggle,
  disabled = false,
  badge,
  currency = "EUR",
  isConnectedUser = false,
  connectedUserTooltip,
}: ArticleRowProps) {
  const options = [
    item.variant?.option1,
    item.variant?.option2,
    item.variant?.option3,
  ]
    .filter(Boolean)
    .filter((o) => o !== "Default Title")
    .join(" • ");

  const catalogPriceCents = parseLineItemPriceInCents(item.price);
  const paidPriceCents = getPaidPriceInCents(item);
  const hasDiscount =
    catalogPriceCents != null && catalogPriceCents !== paidPriceCents;

  return (
    <Article disabled={disabled} onClick={disabled ? undefined : onToggle}>
      {item.image && <ArticleImage src={item.image} alt={item.title} />}
      <ArticleTitle>{item.title}</ArticleTitle>
      {(options || catalogPriceCents != null) && (
        <ArticleSubtitle>
          {options}
          {options && catalogPriceCents != null && " • "}
          {hasDiscount && (
            <span className="line-through">
              {formatPrice(catalogPriceCents, currency)}{" "}
            </span>
          )}
          {catalogPriceCents != null &&
            formatPrice(paidPriceCents, currency)}
        </ArticleSubtitle>
      )}
      {isConnectedUser && item.sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{item.sku}</span>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      <ArticleAction autoHide={false}>
        {badge ? (
          <Badge>{badge}</Badge>
        ) : (
          <Checkbox
            checked={checked}
            onCheckedChange={onToggle}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </ArticleAction>
    </Article>
  );
}

export { ArticleList };
